import { create, StoreApi, UseBoundStore } from 'zustand';

import { SortingState } from '@tanstack/react-table';

import { serializeSort } from './helpers';

export { serializeSort };

// NOTE: this store backs the Inbound Referrals inbox (InboundReferralsListPage) only.
// stores/referralsStore.ts is a separate, legacy Review-typed referrals page — not this one.

export type Tab = 'active' | 'history';

// The chip row's own vocabulary (Active tab only). Deliberately NOT shared with the Status
// dropdown's vocabulary below: 'actionNeeded' here means the per-building queue (queue=true —
// work MY buildings can act on), while the dropdown's 'actionNeeded' option means the
// workflow-derived list_status. Same label, two different params — see
// InboundReferralsListPage's chipParams/statusFilterParams.
export type StatusChip = 'all' | 'actionNeeded' | 'readyForReview';

// The Status dropdown's own vocabulary (all tabs; STATUS_OPTIONS in InboundReferralsListPage).
export type StatusFilterValue = 'all' | 'actionNeeded' | 'processing' | 'readyForReview' | 'complete';

// A home for the dropdown's selection (and any future filter the "Filters" button adds),
// mirroring admissionsStore's `filters` shape.
export type InboundReferralsFilterState = { status?: StatusFilterValue };

type InboundReferralsActions = {
  setFilters: (
    filters: InboundReferralsFilterState | ((prev: InboundReferralsFilterState) => InboundReferralsFilterState)
  ) => void;
  setSort: (sort: SortingState) => void;
  setSearch: (search: string) => void;
  setTab: (tab: Tab) => void;
  setStatusChip: (chip: StatusChip) => void;
};

export type InboundReferralsState = {
  filters: InboundReferralsFilterState;
  sort: SortingState;
  search: string;
  tab: Tab;
  statusChip: StatusChip;
  actions: InboundReferralsActions;
};

const createInboundReferralsStore = (): [
  useStore: UseBoundStore<StoreApi<InboundReferralsState>>,
  useActions: () => InboundReferralsActions,
] => {
  const useStore = create<InboundReferralsState>((set) => ({
    filters: {},
    sort: [{ id: 'createdAt', desc: true }],
    search: '',
    tab: 'active',
    statusChip: 'all',
    actions: {
      setFilters: (filters) =>
        set((state) => ({
          filters: typeof filters === 'function' ? filters(state.filters) : filters,
        })),
      setSort: (sort) => set({ sort }),
      setSearch: (search) => set({ search }),
      setTab: (tab) => set({ tab }),
      setStatusChip: (statusChip) => set({ statusChip }),
    },
  }));

  const useActions = () => useStore((state) => state.actions);

  return [useStore, useActions];
};

export const [useInboundReferralsStore, useInboundReferralsActions] = createInboundReferralsStore();
